import React from 'react';
import PropTypes from 'prop-types';
import Form from '../../Form/Form';
import ModalForm from '../../ModalForm';
import noop from '../../../util/noop';
import useConfirmAction from '../../../hooks/useConfirmAction';
import useContainerProps from '../../../hooks/useContainerProps';
import JsonApiRecordDecorator from '../../../json-api/JsonApiRecordDecorator';

/** renders a form module intended for use inside a section */
const FormModule = props => {
  props = useContainerProps(props);

  const { 
    children,
    confirmCancel = { disabled: true },
    confirmSave = { disabled: true },
    createMember = noop,
    currentEntity,
    isModal,
    noPaddingButtons,
    onCancel,
    onError,
    onSuccess,
    selectMember = noop,
    updateMember = noop
  } = props;

  async function performSaveAction(type, data) {
    let action = noop;
    switch (type) {
    case 'create':
      action = createMember;
      break;
    case 'update':
      action = updateMember;
      break;
    }
    try {
      const response = await action(
        Object.assign(
          new JsonApiRecordDecorator(),
          currentEntity,
          data
        )
      );
      if (response) {
        selectMember(null);
        if (onSuccess) onSuccess();
      }
    } catch(e) {
      if (onError) onError();
    }
  }

  function performCancelAction() {
    if (onCancel) {
      onCancel();
    } else {
      selectMember(null);
    }
  }

  const { action: saveAction, confirmationModal: saveConfirmationModal } = useConfirmAction({
    action: performSaveAction,
    children: confirmSave.children,
    disabled: confirmSave.disabled,
    title: confirmSave.title
  });
  const { action: cancelAction, confirmationModal: cancelConfirmationModal } = useConfirmAction({
    action: performCancelAction,
    children: confirmCancel.children,
    disabled: confirmCancel.disabled,
    title: confirmCancel.title
  });

  const createAction = data => saveAction('create', data);
  const updateAction = data => saveAction('update', data);

  const FormComponent = isModal ? ModalForm : Form;

  return (
    <>
      {currentEntity &&
      <FormComponent
        {...props}
        data={currentEntity}
        onCancel={cancelAction}
        addAction={createAction}
        updateAction={updateAction}
        buttonGroupClass={`modal-footer ${noPaddingButtons ? 'no-padding' : ''}`}
      >
        {children}
      </FormComponent>}
      {saveConfirmationModal}
      {cancelConfirmationModal}
    </>
  );
};

FormModule.propTypes = {
  children: PropTypes.node,
  confirmCancel: PropTypes.shape({
    title: PropTypes.string,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]),
    disabled: PropTypes.bool
  }),
  confirmSave: PropTypes.shape({
    title: PropTypes.string,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]),
    disabled: PropTypes.bool
  }),
  createMember: PropTypes.func,
  currentEntity: PropTypes.object,
  isModal: PropTypes.bool,
  noPaddingButtons: PropTypes.bool,
  onCancel: PropTypes.func,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
  router: PropTypes.shape({
    goBack: PropTypes.func
  }),
  selectMember: PropTypes.func,
  updateMember: PropTypes.func
};

FormModule.defaultProps = {
  saveAndAddButton: false
};

export default FormModule;
